import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import CyberLayout from '@/components/CyberLayout';
import { PhotoSection } from '@/components/PhotoSection';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Image as ImageIcon, User, UserSquare2, AlertCircle, Heart, ShieldAlert } from 'lucide-react';

const texts = {
  it: {
    title: 'Foto dell\'Indagato',
    subtitle: 'DOCUMENTAZIONE FOTOGRAFICA',
    instructions: 'Carica almeno 2 foto recenti dell\'indagato:',
    facePhoto: 'Una foto in cui si vede bene il VISO',
    bodyPhoto: 'Una foto in cui si vede il CORPO INTERO',
    additionalPhotos: 'Puoi aggiungere altre foto utili (luoghi frequentati, veicolo, ecc.)',
    uploadHint: 'Clicca o trascina per caricare',
    maxSize: 'Max 10MB per foto • Tutti i formati (JPG, PNG, HEIC...)',
    photoLabels: ['Foto Viso', 'Foto Corpo', 'Foto 3', 'Foto 4', 'Foto 5'],
    warningMin: 'Carica almeno 2 foto (viso e corpo intero)',
    photos: 'foto caricate',
    loverTitle: 'Foto del Presunto Amante',
    loverInstructions: 'Se disponibili, carica foto del presunto amante (facoltativo). Sono utili per riconoscerlo durante l\'indagine.',
    loverLabel: 'Amante',
    evidenceTitle: 'Prove Raccolte',
    evidenceInstructions: 'Carica screenshot di chat, post, commenti, messaggi o foto che documentano gli episodi (facoltativo, max 5).',
    evidenceLabel: 'Prova',
    bullyTitle: 'Foto del Presunto Bullo',
    bullyInstructions: 'Se disponibili, carica foto recenti del presunto bullo (facoltativo):'
  },
  en: {
    title: 'Subject Photos',
    subtitle: 'PHOTOGRAPHIC DOCUMENTATION',
    instructions: 'Upload at least 2 recent photos of the subject:',
    facePhoto: 'One photo clearly showing the FACE',
    bodyPhoto: 'One photo showing the FULL BODY',
    additionalPhotos: 'You can add other useful photos (frequented places, vehicle, etc.)',
    uploadHint: 'Click or drag to upload',
    maxSize: 'Max 10MB per photo • All formats (JPG, PNG, HEIC...)',
    photoLabels: ['Face Photo', 'Body Photo', 'Photo 3', 'Photo 4', 'Photo 5'],
    warningMin: 'Upload at least 2 photos (face and full body)',
    photos: 'photos uploaded',
    loverTitle: 'Suspected Lover Photos',
    loverInstructions: 'If available, upload photos of the suspected lover (optional). They help identify them during the investigation.',
    loverLabel: 'Lover',
    evidenceTitle: 'Collected Evidence',
    evidenceInstructions: 'Upload screenshots of chats, posts, comments, messages or photos documenting the incidents (optional, max 5).',
    evidenceLabel: 'Evidence',
    bullyTitle: 'Alleged Bully Photos',
    bullyInstructions: 'If available, upload recent photos of the alleged bully (optional):'
  }
};

const restore = (key) => JSON.parse(sessionStorage.getItem(key) || '[]').map(d => ({ data: d, preview: d }));

export default function PhotoUpload() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [photos, setPhotos] = useState(() => restore('photos'));
  const [loverPhotos, setLoverPhotos] = useState(() => restore('loverPhotos'));
  const [evidencePhotos, setEvidencePhotos] = useState(() => restore('evidencePhotos'));
  const [isInfidelity, setIsInfidelity] = useState(false);
  const [isBullying, setIsBullying] = useState(false);
  const txt = texts[language] || texts.it;

  useEffect(() => {
    const typeData = sessionStorage.getItem('selectedType');
    if (!sessionStorage.getItem('privacyAccepted') || !sessionStorage.getItem('answers') || !typeData) {
      navigate('/select-type');
      return;
    }
    const type = JSON.parse(typeData);
    setIsInfidelity(/coniugale/i.test(type.name_it || '') || /marital/i.test(type.name_en || ''));
    setIsBullying(/bullismo/i.test(type.name_it || '') || /bullying/i.test(type.name_en || ''));
  }, [navigate]);

  const handleContinue = () => {
    if (!isBullying && photos.length < 2) {
      toast.warning(language === 'it'
        ? 'Si consiglia di caricare almeno 2 foto (viso e corpo intero)'
        : 'It is recommended to upload at least 2 photos (face and full body)');
    }
    sessionStorage.setItem('photos', JSON.stringify(photos.map(p => p.data)));
    sessionStorage.setItem('loverPhotos', JSON.stringify(isInfidelity ? loverPhotos.map(p => p.data) : []));
    sessionStorage.setItem('evidencePhotos', JSON.stringify(isBullying ? evidencePhotos.map(p => p.data) : []));
    navigate('/summary');
  };

  const sectionProps = { uploadHint: txt.uploadHint, maxSize: txt.maxSize, language, t };

  return (
    <CyberLayout>
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-20">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8 fade-in-up">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyber-gray border border-neon-cyan/30 mb-6">
              <ImageIcon className="w-8 h-8 text-neon-cyan" />
            </div>
            <h1 className="font-orbitron text-2xl sm:text-3xl font-bold text-white mb-2 tracking-wider">{isBullying ? txt.bullyTitle : txt.title}</h1>
            <p className="font-mono text-neon-cyan text-sm tracking-widest uppercase">{txt.subtitle}</p>
          </div>

          <div className="cyber-card rounded-lg p-6 mb-6 fade-in-up border-l-4 border-neon-green" style={{ animationDelay: '0.1s' }}>
            <p className="font-mono text-white text-sm mb-4">{isBullying ? txt.bullyInstructions : txt.instructions}</p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-neon-green/20 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-neon-green" />
                </div>
                <span className="font-mono text-sm text-gray-300">1. {txt.facePhoto}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-neon-cyan/20 flex items-center justify-center flex-shrink-0">
                  <UserSquare2 className="w-4 h-4 text-neon-cyan" />
                </div>
                <span className="font-mono text-sm text-gray-300">2. {txt.bodyPhoto}</span>
              </div>
            </div>
            <p className="font-mono text-xs text-gray-500 mt-4">{txt.additionalPhotos}</p>
          </div>

          <PhotoSection
            {...sectionProps}
            photos={photos}
            setPhotos={setPhotos}
            testIdPrefix="photo"
            labels={{
              nameFor: (i) => txt.photoLabels[i] || `Foto ${i + 1}`,
              colorFor: (i) => (i === 0 ? 'bg-neon-green/80' : i === 1 ? 'bg-neon-cyan/80' : 'bg-gray-600/80'),
              countText: txt.photos
            }}
          />
          {!isBullying && photos.length < 2 && (
            <div className="flex justify-center -mt-4 mb-6">
              <div className="inline-flex items-center gap-2 text-yellow-500 font-mono text-xs bg-yellow-500/10 px-3 py-1.5 rounded" data-testid="photo-min-warning">
                <AlertCircle className="w-4 h-4" />
                {txt.warningMin}
              </div>
            </div>
          )}

          {isInfidelity && (
            <div data-testid="lover-photos-section" className="fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="cyber-card rounded-lg p-6 mb-6 border-l-4 border-pink-500">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-4 h-4 text-pink-400" />
                  </div>
                  <h2 className="font-orbitron text-base text-white tracking-wider">{txt.loverTitle}</h2>
                </div>
                <p className="font-mono text-xs text-gray-400">{txt.loverInstructions}</p>
              </div>
              <PhotoSection
                {...sectionProps}
                photos={loverPhotos}
                setPhotos={setLoverPhotos}
                testIdPrefix="lover-photo"
                labels={{ nameFor: (i) => `${txt.loverLabel} ${i + 1}`, colorFor: () => 'bg-pink-600/80', countText: txt.photos }}
              />
            </div>
          )}

          {isBullying && (
            <div data-testid="evidence-photos-section" className="fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="cyber-card rounded-lg p-6 mb-6 border-l-4 border-yellow-500">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                    <ShieldAlert className="w-4 h-4 text-yellow-400" />
                  </div>
                  <h2 className="font-orbitron text-base text-white tracking-wider">{txt.evidenceTitle}</h2>
                </div>
                <p className="font-mono text-xs text-gray-400">{txt.evidenceInstructions}</p>
              </div>
              <PhotoSection
                {...sectionProps}
                photos={evidencePhotos}
                setPhotos={setEvidencePhotos}
                testIdPrefix="evidence-photo"
                labels={{ nameFor: (i) => `${txt.evidenceLabel} ${i + 1}`, colorFor: () => 'bg-yellow-600/80', countText: txt.photos }}
              />
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 fade-in-up" style={{ animationDelay: '0.4s' }}>
            <button
              onClick={() => navigate('/questionnaire')}
              data-testid="photos-back-btn"
              className="cyber-btn-outline px-6 py-3 flex items-center justify-center gap-2 rounded-md flex-1"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('back')}</span>
            </button>
            <button
              onClick={handleContinue}
              data-testid="continue-summary-btn"
              className={`cyber-btn px-6 py-3 flex items-center justify-center gap-2 flex-1 ${photos.length < 2 ? 'opacity-70' : ''}`}
            >
              <span>{t('continueToSummary')}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </CyberLayout>
  );
}
